'use client';

import { useEffect, useRef, useState } from 'react';

const DEFAULT_INTERSECTION_OBSERVER_ROOT_MARGIN = '100px';

/**
 * Options for configuring the Intersection Observer
 */
export interface UseIntersectionObserverOptions {
  /**
   * Margin around the viewport (e.g., '100px', '50px 0px')
   * @default '100px'
   */
  rootMargin?: string;
  /**
   * Visibility percentage to trigger intersection (0.0 to 1.0)
   * @default 0.1
   */
  threshold?: number | number[];
}

/**
 * Result from the useIntersectionObserver hook
 */
export interface IntersectionObserverResult<T extends HTMLElement = HTMLElement> {
  /** Ref to attach to the target element */
  ref: React.RefObject<T | null>;
  /** Whether the element is currently visible in the viewport */
  isIntersecting: boolean;
}

/**
 * Detects when an element enters the viewport
 *
 * @param options - Observer configuration
 * @param options.rootMargin - Margin around the viewport (default: '100px')
 * @param options.threshold - Visibility percentage to trigger (default: 0.1)
 * @returns Ref and intersection state
 *
 * @example
 * ```tsx
 * const { ref, isIntersecting } = useIntersectionObserver({
 *   rootMargin: '50px',
 *   threshold: 0.5
 * });
 *
 * return <div ref={ref}>{isIntersecting ? 'Visible' : 'Hidden'}</div>;
 * ```
 *
 * @example
 * ```tsx
 * // For specific element types like img
 * const { ref, isIntersecting } = useIntersectionObserver<HTMLImageElement>({
 *   rootMargin: '50px',
 *   threshold: 0.5
 * });
 *
 * return <img ref={ref} src="..." />;
 * ```
 */
const useIntersectionObserver = <T extends HTMLElement = HTMLElement>({
  rootMargin = DEFAULT_INTERSECTION_OBSERVER_ROOT_MARGIN,
  threshold = 0.1,
}: UseIntersectionObserverOptions): IntersectionObserverResult<T> => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef<T>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsIntersecting(true);

            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin,
        threshold,
      },
    );

    const element = ref.current;

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }

      observer.disconnect();
    };
  }, [rootMargin, threshold]);

  return { ref, isIntersecting };
};

export { useIntersectionObserver };
